import { Field } from 'formik';
import { Row, Form, Badge, Col } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { useFormikContext } from 'formik';

import { useAppSelector } from 'hooks';
import { CareProgramTypes } from 'constants/enums';
import { getCarePrograms } from 'store/groups/reducer';

import { IInvitation } from '../Onboard';

interface ISelectProgramProps {
  gid: string;
}

const SelectPrograms = ({ gid }: ISelectProgramProps) => {
  const intl = useIntl();
  const { values } = useFormikContext<IInvitation>();
  const programs = useAppSelector((state) => getCarePrograms(state, gid));

  const sortPrograms = (programA: ICareProgram, programB: ICareProgram) => {
    const localizedAName = intl.formatMessage({ id: programA.name }).toLowerCase();
    const localizedBName = intl.formatMessage({ id: programB.name }).toLowerCase();
    if (programA.name === 'RPMKey') return -1;
    else if (programB.name === 'RPMKey') return 1;
    else return localizedAName.localeCompare(localizedBName);
  };

  const managementPrograms = programs
    .filter((program) => program.type === CareProgramTypes.MANAGEMENT)
    .sort((a, b) => sortPrograms(a, b));
  const monitoringPrograms = programs
    .filter((program) => program.type === CareProgramTypes.MONITORING)
    .sort((a, b) => sortPrograms(a, b));
  const coachingPrograms = programs
    .filter((program) => program.type === CareProgramTypes.COACHING)
    .sort((a, b) => sortPrograms(a, b));

  const isProgramUniqueMonitoringProgram = (program: ICareProgram) => {
    const patientMonitoringPrograms = values.patientCareProgram.filter((cpid) =>
      monitoringPrograms.find((monitoringProgram) => monitoringProgram.cpid === cpid)
    );
    return patientMonitoringPrograms.length === 1 && patientMonitoringPrograms[0] !== program.cpid;
  };

  const renderPrograms = (programs: ICareProgram[]) => {
    return programs.map((program, key) => {
      const disableSelection =
        program.type === CareProgramTypes.MONITORING && isProgramUniqueMonitoringProgram(program);
      const isRPM = program.name === 'RPMKey';
      return (
        <Row key={key}>
          <Form.Label className="w-100">
            <Form.Group className={`form-group d-flex ${isRPM ? 'bg-light py-2 br-5' : ''}`}>
              <div className="cursor-pointer d-flex flex-nowrap ps-4">
                <Field
                  type="checkbox"
                  name="patientCareProgram"
                  value={program.cpid}
                  data-test={program.name}
                  disabled={disableSelection}
                />
              </div>
              <dl className="m-0 max-w-90p ps-2 text-nm">
                <dt>
                  {intl.formatMessage({ id: program.name })}{' '}
                  {isRPM && (
                    <Badge bg="secondary" className="ms-2">
                      {intl.formatMessage({ id: 'highlyUtilizedKey' })}
                    </Badge>
                  )}
                </dt>
                <dd className="text-sm">
                  {intl.formatMessage({ id: `${program.name}_description` })}
                </dd>
              </dl>
            </Form.Group>
          </Form.Label>
        </Row>
      );
    });
  };

  return (
    <>
      <h4 className="mb-4" data-test="select-program-title">
        {intl.formatMessage({ id: 'selectProgramsKey' })}
      </h4>
      {managementPrograms.length + monitoringPrograms.length > 1 && (
        <div
          className="d-flex align-items-center p-4 bg-blue-light mb-4 br-5"
          data-test="cms-rules-info"
        >
          <div className="ms-2">{intl.formatMessage({ id: 'CMSRulesKey' })}</div>
        </div>
      )}
      <Col>
        {monitoringPrograms.length > 0 && (
          <>
            <p className="d-flex font-weight-bolder text-lg" data-test="management-program">
              {intl.formatMessage({ id: 'monitoringKey' })}
              <Badge bg="primary" className="ms-2">
                {intl.formatMessage({ id: 'devicesEnabledKey' })}
              </Badge>
            </p>
            {renderPrograms(monitoringPrograms)}
          </>
        )}

        {managementPrograms.length > 0 && (
          <>
            <p className="font-weight-bolder text-lg" data-test="monitoring-program">
              {intl.formatMessage({ id: 'careManagementKey' })}
            </p>
            {renderPrograms(managementPrograms)}
          </>
        )}

        {coachingPrograms.length > 0 && (
          <>
            <p className="font-weight-bolder text-lg" data-test="coaching-program">
              {intl.formatMessage({ id: 'coachingProgramKey' })}
            </p>
            {renderPrograms(coachingPrograms)}
          </>
        )}
      </Col>
    </>
  );
};

export default SelectPrograms;
